import { InvestorDetailsCommitmentsTabTableDataQuery, InvestorDetailsHouseholdCommitmentsTabTableDataQuery } from '@/__generated__/graphql';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { TableColumns } from '@/components/table/types';
import { formatCash } from '@/shared/utils/currency';
import { useMemo } from 'react';
import styled from 'styled-components';
import { InvestorDetailsEntityTypeEnum } from '../../../types';

type RowSumType = {
  total: string;
  committedCapital: number;
  capitalCalled: number;
  capitalRemaining: number;
  distributionsToDate: number;
  marketValue: number;
};

interface CommitmentsTableSumFooterProps {
  tableColumns: TableColumns[];
  tableRows: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'];
  entityType: InvestorDetailsEntityTypeEnum;
  householdCommitmentsTabTableTotals: InvestorDetailsHouseholdCommitmentsTabTableDataQuery['investorHouseholdCommitments']['totals'] | null;
}

const CommitmentsTableSumFooter = ({ tableColumns, tableRows, entityType, householdCommitmentsTabTableTotals }: CommitmentsTableSumFooterProps) => {
  const rowSum: RowSumType = useMemo(() => {
    if (entityType === InvestorDetailsEntityTypeEnum.Household) {
      const { committedCapital, capitalCalled, capitalRemaining, distributionsToDate, marketValue } = householdCommitmentsTabTableTotals || {};
      return {
        total: 'Total',
        committedCapital: committedCapital || 0,
        capitalCalled: capitalCalled || 0,
        capitalRemaining: capitalRemaining || 0,
        distributionsToDate: distributionsToDate || 0,
        marketValue: marketValue || 0
      };
    }
    const sum = {
      total: 'Total',
      committedCapital: 0,
      capitalCalled: 0,
      capitalRemaining: 0,
      distributionsToDate: 0,
      marketValue: 0
    };
    for (let i = 0; i < tableRows.length; i++) {
      sum.committedCapital += tableRows[i].committedCapital;
      sum.capitalCalled += tableRows[i].capitalCalled;
      sum.capitalRemaining += tableRows[i].capitalRemaining;
      sum.distributionsToDate += +tableRows[i].distributionsToDate;
      sum.marketValue += +tableRows[i].marketValue;
    }
    return sum;
  }, [tableRows, entityType, householdCommitmentsTabTableTotals]);

  const toolTipContent = (row: RowSumType) => {
    return (
      <TableBodyWrapper>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.committedCapital)} Committed Capital</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalCalled)} Capital Called</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalRemaining)} Capital Remaining</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          {entityType === InvestorDetailsEntityTypeEnum.Household ? (
            <TextCell>{householdCommitmentsTabTableTotals?.capitalCalledPercentage?.toFixed(2)}% Called to Date</TextCell>
          ) : (
            <TextCell>{((row.capitalCalled / row.committedCapital) * 100).toFixed(2)}% Called to Date</TextCell>
          )}
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  return (
    <TableSumWrap>
      <TableSumItem width={tableColumns[0].width}>{rowSum.total}</TableSumItem>
      <TableSumItem width={tableColumns[1].width}></TableSumItem>
      <TableSumItem width={tableColumns[2].width}>
        <FormattingTooltip>{rowSum.committedCapital}</FormattingTooltip>
      </TableSumItem>
      <TableSumItem width={tableColumns[3].width} cursor={'pointer'}>
        <Tooltip title={toolTipContent(rowSum)}>
          {entityType === InvestorDetailsEntityTypeEnum.Household ? (
            <>{householdCommitmentsTabTableTotals?.capitalCalledPercentage?.toFixed(2)}%</>
          ) : (
            <>
              {rowSum.capitalCalled === 0 || rowSum.committedCapital === 0
                ? '0%'
                : ((rowSum.capitalCalled / rowSum.committedCapital) * 100).toFixed(2) + '%'}
            </>
          )}
        </Tooltip>
      </TableSumItem>
      <TableSumItem width={tableColumns[4].width}>
        <FormattingTooltip>{rowSum.distributionsToDate}</FormattingTooltip>
      </TableSumItem>
      <TableSumItem width={tableColumns[5].width}>
        <FormattingTooltip>{rowSum.marketValue}</FormattingTooltip>
      </TableSumItem>
      <TableSumItem width={tableColumns[6].width}></TableSumItem>
    </TableSumWrap>
  );
};

export default CommitmentsTableSumFooter;

const TableSumWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 60px;
  background-color: ${({ theme }) => theme.layer[2]};
  border-radius: 8px;
  padding: 0 24px;
`;

const TableSumItem = styled.div<{ width: number; cursor?: string }>`
  display: flex;
  justify-content: flex-start;
  width: ${({ width }) => `${width}%`};
  color: ${({ theme }) => theme.font.base};
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  cursor: ${({ cursor }) => cursor};
`;
